import {
    Button,
    Checkbox,
    ChoiceList,
    Stack,
    TextField,
    TextStyle,
} from "@shopify/polaris";
import get from "lodash/get";
import React, { lazy } from "react";
import styled from "styled-components";

import { useQuery } from "@apollo/react-hooks";
import { useAppContext } from "../../../context";
import {
    arrInvalid,
    checkPlatform,
    isPME_TEAM,
    objectInvalid,
    reducerFn,
} from "../../../helper";
import {
    MAX_LENGTH,
    MERCHIZE_PRINTING_METHOD_OPTIONS,
    TITLE_LENGTH_MESS,
} from "../../../variable";
import {
    getPGShippingMethod,
    getPrintifyShippingMethod,
    getPrintwayShippingMethods,
} from "../../base/utils";
import FillAllCustomField from "../../customize/fill-all-custom-field";
import { checkFulfillment } from "../../order/ManualOrder/components/Form";
import SuspenseComp from "../../shared/SuspenseComp";
import { useMapActionContext } from "../context";
import {
    getActiveVariants,
    getAttributeState,
    getMetaData,
    showCarrierSelect,
} from "../utils";
import { BaseFormProvider } from "./context";

const BaseBox = lazy(() => import("./BaseBox"));
const Fulfillment = lazy(() => import("./Fulfillment"));
const ShippingService = lazy(() => import("../ShippingService"));
const AttributesCTComp = lazy(() => import("../SetAttributesPolaris"));
const OrderLabel = lazy(() => import("../components/PBaseOrderLabel"));
const MappingVariants = lazy(() => import("../MappingVariantsPolaris"));
const TagsSelect = lazy(() => import("../../product/tags/TagsSelectPolaris"));
const CollectionSelect = lazy(() =>
    import("../../product/collections/CollectionSelectPolaris"),
);
const SimpleCustomize = lazy(() =>
    import("../../customize/SimpleCustomizePolaris"),
);
const ViewPersonalizedInfo = lazy(() =>
    import("../components/ViewPersonalizedInfo"),
);

const CarrierSelect = lazy(() =>
    import("../../carrier/components/carrier-select-by-variant"),
);

// collection and tag's prop
const propsCommon = {
    allowMultiple: true,
    haveQuickAdd: true,
    required: false,
    limit: 200,
};

export default function MapToBaseForm({ loading, onSubmit, setLoading }) {
    const { item } = useMapActionContext();

    const { currentUser } = useAppContext();
    const isPMETeam = isPME_TEAM(currentUser);

    const itemVariants = get(item, "variants") || [];
    const platform = get(item, "store.platform");
    const { isTiktok, isAmazon, isShopify, isWooCommerce, isEtsy } =
        checkPlatform(platform);

    const title = get(item, "product.name");
    const quantity = get(item, "quantity") + "";

    const [state, setState] = React.useReducer(reducerFn, {
        fulfillment: null,
        base: null,
        productTitle: title || "",
        shippingOption: null,
        baseVariants: [],

        warehouse: [],
        forceMapping: false,
        mapToBaseVariantId: null,
        autoMapLaterOrders: isPMETeam && isTiktok,
        mappingVariant: [],
        activateVariants: [],

        simpleChecked: false,
        groupChecked: [],
        groupItems: [],

        collections: [],
        tags: [],
        checkPersonalized: false,
        fields: [],

        errors: {},
        printingMethod: [""],
        quantity: quantity,

        metaValue: undefined,
        selectedShippingOption: [],
    });

    const productBaseId = get(state, "base.id");

    const [fulfillmentInfo, setFulfillmentInfo] = React.useReducer(reducerFn, {
        isPrintway: false,
        isPrintify: false,
        isMerchize: false,
        isPGPrints: false,
        shippingMethods: [],
    });

    const handleStateChange = React.useCallback((state) => {
        let resetShippingOption = false;
        // update shipping methods state
        // if (state?.fulfillment) {
        if ("fulfillment" in state) {
            state.base = null;
            resetShippingOption = true;
            setFulfillmentInfo(getShippingMethods(state.fulfillment));
        }

        if (state?.base) {
            state.baseVariants = getBaseVariants(state.base);
            resetShippingOption = true;

            const { defaultShippingOption } = getShippingOptions(state);
            if (defaultShippingOption) {
                state.selectedShippingOption = [defaultShippingOption];
            }
        }

        if (resetShippingOption) {
            state.shippingOption = null;
        }
        // update shipping option
        setState(state);
    }, []);

    const handleValidateTitle = React.useCallback((value, id) => {
        const err = validateTitle(value);
        setState((p) => ({ ...p, errors: { ...p.errors, [id]: err } }));
    }, []);

    const handleValueChange = React.useCallback(
        (_id) => (value, id) => {
            const mergeID = _id || id;

            const newState = { [mergeID]: value };
            if (mergeID == "checkPersonalized" && !value) {
                newState.fields = [];
            }

            setState(newState);

            if (mergeID === "productTitle") {
                handleValidateTitle(value, mergeID);
            }
        },
        [],
    );

    const handleUpdateBaseFromWarehouse = React.useCallback((newState) => {
        setState((p) => {
            const { variants, ...restState } = newState;
            if (variants && p.base) {
                p.base.variants = variants;
            }

            return {
                ...p,
                ...restState,
            };
        });
    }, []);

    const { baseVariants, baseID } = React.useMemo(
        () => getBaseInfo(state.base),
        [state.base],
    );

    const handleGetShippingMethods = React.useCallback(
        async (newVariants) => {
            if (fulfillmentInfo.isPrintway) {
                const opts = await getPrintwaySM(newVariants, baseVariants);
                setFulfillmentInfo({ shippingMethods: opts });
            }

            if (fulfillmentInfo.isPrintify) {
                const opts = await getPrintifySM(baseID);
                setFulfillmentInfo({ shippingMethods: opts });
            }

            if (fulfillmentInfo.isPGPrints) {
                const opts = await getPGPrintsSM(baseID);
                setFulfillmentInfo({ shippingMethods: opts });
            }
        },
        [
            fulfillmentInfo.isPrintify,
            fulfillmentInfo.isPrintway,
            baseVariants,
            baseID,
        ],
    );

    const handleVariantChange = React.useCallback(
        (newValues) => {
            const activateVariants = getActiveVariants(newValues);
            setState({
                mappingVariant: newValues,
                activateVariants,
                shippingOption: null,
            });

            handleGetShippingMethods(newValues);
        },
        [handleGetShippingMethods],
    );

    const handleBaseVariantChange = React.useCallback(
        (id) => {
            if (state.mapToBaseVariantId == id) return;
            setState({ mapToBaseVariantId: id });
        },
        [state.mapToBaseVariantId],
    );

    const handleAttributeChange = React.useCallback((data) => {
        let newState = getAttributeState(data);
        setState(newState);
    }, []);

    const handleErrChange = React.useCallback((value) => {
        setState((p) => ({
            ...p,
            errors: { ...p.errors, errPersonal: value },
        }));
    }, []);

    const showPersonalized =
        isAmazon || isShopify || isEtsy || isWooCommerce || isTiktok;
    const isPersonalizedDisable = hasMBPersonalized(item);
    const metaData = getMetaData(item);
    let showAutoMap = isAmazon || isEtsy || isTiktok;
    if (isPMETeam) {
        showAutoMap = isTiktok;
    }

    // Input
    const mappingOrderId = get(item, "id");

    const mappingVariant = sortVariants(get(state, "mappingVariant"));
    const personalized = isPersonalizedDisable || state.checkPersonalized;
    const { fieldValues, fields } = getFieldInfoToSubmit(get(state, "fields"));

    const carrierRef = React.useRef();

    const [shippingOption] = state.selectedShippingOption || [];
    const handleSubmit = React.useCallback(() => {
        let carrierID = undefined;
        if (carrierRef.current && carrierRef.current.getCarrier) {
            carrierID = (carrierRef.current.getCarrier() || [])[0];
        }

        const err = handleValidateTitle(state.productTitle);
        if (err || !state.productTitle) return;

        const input = {
            mappingOrderId,
            productBaseId,
            mappingVariant: state.forceMapping ? [] : mappingVariant,
            personalized,
            fields,
            fieldValues,
            productTitle: state.productTitle,
            collectionIds: state.collections,
            tagIds: state.tags,

            activeVariants: state.forceMapping ? [] : state.activateVariants,
            shippingOption: shippingOption
                ? shippingOption
                : state.shippingOption,
            forceMapping: state.forceMapping,
            mapToBaseVariantId: state.forceMapping
                ? state.mapToBaseVariantId
                : undefined,
            autoMapLaterOrders: showAutoMap
                ? state.autoMapLaterOrders
                : undefined,
            fulfillmentWarehouse: state.warehouse
                ? state.warehouse[0]
                : undefined,
            carrierID,
            printingMethod: state.printingMethod[0] || undefined,
            // quantity:
            //     state.quantity && Number(state.quantity) > 0
            //         ? Number(state.quantity)
            //         : undefined,
        };

        // onSubmit && onSubmit(input);
        if (onSubmit) {
            onSubmit(input);
            setLoading(true);
        }
    }, [
        mappingOrderId,
        productBaseId,
        fieldValues,
        fields,
        mappingVariant,
        personalized,

        state.mappingVariant,
        state.collections,
        state.tags,
        state.activateVariants,
        state.productTitle,
        state.forceMapping,
        state.shippingOption,
        state.mapToBaseVariantId,
        state.autoMapLaterOrders,
        state.warehouse,
        state.printingMethod,
        state.quantity,
        shippingOption,

        showAutoMap,
        onSubmit,
        setLoading,
    ]);

    const autoMapMarkup = showAutoMap ? (
        <Stack vertical spacing="none">
            <Checkbox
                checked={state.autoMapLaterOrders}
                label="Auto mapping for later orders"
                id="autoMapLaterOrders"
                onChange={handleValueChange()}
            />
            <span style={{ display: "block", marginTop: "-2px" }}>
                <TextStyle variation="subdued">
                    Recommend for non-custom orders
                </TextStyle>
            </span>
        </Stack>
    ) : null;

    const disabled = React.useMemo(() => {
        let res = true;

        let validCollectionTag = true;

        if (isPMETeam) {
            validCollectionTag =
                !arrInvalid(state.collections) && !arrInvalid(state.tags);
        }

        if (
            !arrInvalid(state.activateVariants) &&
            // !arrInvalid(state.collections) &&
            // !arrInvalid(state.tags)
            validCollectionTag
        ) {
            res = false;

            res = (() => {
                if (!state.checkPersonalized) return false;
                if (arrInvalid(state.fields)) return true;
                if (state.errors["errPersonal"]) {
                    return getErrPersonal(state.errors["errPersonal"]);
                }
                return false;
            })();
        }

        return res;
    }, [
        state.activateVariants,
        state.collections,
        state.tags,

        state.checkPersonalized,
        state.fields,
        state.errors,
    ]);
    const { shippingOptions } = getShippingOptions(state);

    if (isPMETeam) {
        propsCommon.required = true;
    }

    return (
        <Wrapper>
            <BaseFormProvider {...state} setFormState={handleStateChange}>
                <Stack vertical>
                    <Stack>
                        <Stack.Item fill>
                            <SuspenseComp>
                                <Fulfillment />
                            </SuspenseComp>
                        </Stack.Item>
                        <Stack.Item fill>
                            <SuspenseComp>
                                <BaseBox />
                            </SuspenseComp>
                        </Stack.Item>
                    </Stack>

                    <TextField
                        id="productTitle"
                        label="Product title"
                        value={state.productTitle}
                        onChange={handleValueChange()}
                        error={state.errors["productTitle"]}
                    />

                    {fulfillmentInfo.isMerchize && !!state.base ? (
                        <SuspenseComp>
                            <OrderLabel
                                warehouse={state.warehouse}
                                originVariants={state.baseVariants}
                                setState={handleUpdateBaseFromWarehouse}
                            />
                        </SuspenseComp>
                    ) : null}

                    <SuspenseComp>
                        <MappingVariants
                            item={item}
                            productBaseId={baseID}
                            variants={itemVariants}
                            productBaseVariants={baseVariants}
                            forceMapping={state.forceMapping}
                            setForceMapping={handleValueChange("forceMapping")}
                            setMapToBaseVariantId={handleBaseVariantChange}
                            onChange={handleVariantChange}
                            autoMapMarkup={autoMapMarkup}
                        />
                    </SuspenseComp>

                    <SuspenseComp>
                        <AttributesCTComp
                            productBase={state.base}
                            activateVariants={state.activateVariants}
                            onChange={handleAttributeChange}
                            simpleChecked={state.simpleChecked}
                            groupChecked={state.groupChecked}
                            groupItems={state.groupItems}
                        />
                    </SuspenseComp>

                    <SuspenseComp>
                        <CollectionSelect
                            {...propsCommon}
                            label="Collections"
                            value={state.collections}
                            onChange={handleValueChange("collections")}
                            hasPopular
                        />
                    </SuspenseComp>
                    <SuspenseComp>
                        <TagsSelect
                            {...propsCommon}
                            label="Tags"
                            value={state.tags}
                            onChange={handleValueChange("tags")}
                        />
                    </SuspenseComp>

                    {/* <TextField
                        label="Quantity"
                        value={state.quantity}
                        type="number"
                        id="quantity"
                        min={1}
                        onChange={handleValueChange()}
                    /> */}

                    {showPersonalized ? (
                        <SuspenseComp>
                            <ViewPersonalizedInfo />
                        </SuspenseComp>
                    ) : null}

                    <div style={{ display: "flex", gap: "2rem" }}>
                        <Checkbox
                            label="This is a personalized product"
                            checked={state.checkPersonalized}
                            id="checkPersonalized"
                            onChange={handleValueChange()}
                            disabled={isPersonalizedDisable}
                        />

                        {state.checkPersonalized ? (
                            <FillAllCustomField
                                hasFieldValue
                                isAmazon={isAmazon}
                                personalized={item?.personalized || metaData}
                                setState={setState}
                            />
                        ) : null}
                    </div>

                    {state.checkPersonalized ? (
                        <SuspenseComp>
                            <SimpleCustomize
                                isMapOrder
                                item={item}
                                value={state.fields}
                                personalized={item?.personalized || metaData}
                                onChange={handleValueChange("fields")}
                                onErrorChange={handleErrChange}
                                metaValue={state.metaValue}
                            />
                        </SuspenseComp>
                    ) : null}

                    <SuspenseComp>
                        <ShippingService
                            item={item}
                            fulfillment={state.fulfillment}
                            shippingOption={state.shippingOption}
                            setShippingOption={handleValueChange(
                                "shippingOption",
                            )}
                            options={fulfillmentInfo.shippingMethods}
                        />
                    </SuspenseComp>

                    {fulfillmentInfo.isMerchize ? (
                        <ChoiceList
                            title="Printing methods"
                            selected={state.printingMethod}
                            onChange={handleValueChange("printingMethod")}
                            choices={MERCHIZE_PRINTING_METHOD_OPTIONS}
                        />
                    ) : null}

                    {showCarrierSelect(state.fulfillment) &&
                    !!state.mapToBaseVariantId ? (
                        <SuspenseComp>
                            <CarrierSelect
                                variantIds={[state.mapToBaseVariantId]}
                                ref={carrierRef}
                            />
                        </SuspenseComp>
                    ) : null}

                    {arrInvalid(shippingOptions) ? null : (
                        <ChoiceList
                            choices={shippingOptions}
                            selected={state.selectedShippingOption}
                            title="Shipping Options"
                            onChange={(selected) =>
                                setState({ selectedShippingOption: selected })
                            }
                        />
                    )}

                    {loading ? (
                        <Button
                            primary
                            fullWidth
                            disabled
                            loading
                            onClick={() => {}}
                            children="Submit"
                        />
                    ) : (
                        <Button
                            primary
                            fullWidth
                            disabled={disabled}
                            loading={loading}
                            onClick={handleSubmit}
                            children="Submit"
                        />
                    )}
                </Stack>
            </BaseFormProvider>
        </Wrapper>
    );
}

function hasMBPersonalized(item) {
    const metaDataArr = get(item, "metaDta", []);
    if (arrInvalid(metaDataArr)) return false;

    const mbKey = /_?mb_personalized/;
    return !!metaDataArr.find((m) => (m.key || "").match(mbKey));
}

function validateTitle(str) {
    let err = "";
    if (!str) err = "Product title is required.";
    if (str.length > MAX_LENGTH) {
        err = TITLE_LENGTH_MESS;
    }
    return err;
}

function getShippingMethods(fulfillment) {
    return { ...checkFulfillment(fulfillment?.slug), shippingMethods: [] };
}

function getBaseVariants(base) {
    return get(base, "variants") || [];
}

function getBaseInfo(base) {
    let res = {
        baseID: null,
        baseVariants: [],
    };
    res.baseVariants = getBaseVariants(base) || [];
    res.baseID = get(base, "id") || null;
    return res;
}

async function getPrintwaySM(variants, baseVariants) {
    const variantID = get(variants, "[0].productBaseVariantId");
    if (!variantID || arrInvalid(baseVariants)) return;

    const match = baseVariants.find((i) => i?.id === variantID);
    if (!match || !match.fulfillmentSku) return [];

    let fulfillmentSku = match.fulfillmentSku;
    const shippingServices = await getPrintwayShippingMethods([fulfillmentSku]);
    if (arrInvalid(shippingServices)) return [];

    const matchById = shippingServices.find(
        (i) => i.itemSKU === fulfillmentSku,
    );
    if (objectInvalid(matchById) || arrInvalid(matchById.shippingServices))
        return [];

    return matchById.shippingServices.map((method) => {
        if (typeof method === "string") {
            return {
                value: method,
                label: method,
            };
        }

        return { value: method.code, label: method.name };
    });
}

async function getPrintifySM(baseID) {
    if (!baseID) return [];
    return await getPrintifyShippingMethod(baseID);
}

async function getPGPrintsSM(baseID) {
    if (!baseID) return [];
    return await getPGShippingMethod(baseID);
}

function getErrPersonal(err) {
    let errs = Object.values(err)
        .map((item) => Object.values(item))
        .flat()
        .filter((i) => i != null);

    return !!errs.length;
}

export function getFieldInfoToSubmit(fields) {
    let values = [];
    let f = [];

    if (fields && Array.isArray(fields)) {
        for (let item of fields) {
            if (!item) continue;
            const { fieldValue, ...rest } = item;

            if (fieldValue?.key) values.push(fieldValue);
            f.push(rest);
        }
    }

    return {
        fieldValues: values,
        fields: f,
    };
}

function sortVariants(variants) {
    return variants
        .sort((a, b) => a.sorting - b.sorting)
        .map((v) => ({
            variantId: v.variantId,
            productBaseVariantId: v.productBaseVariantId,
        }));
}

function getShippingOptions(state) {
    const nodes = get(state, "base.shippingOptions") || [];

    const defaultShippingOptionId =
        get(state, "base.defaultShippingOptionId") || "";

    let defaultShippingOption = "";

    const shippingOptions = [];
    for (let node of nodes) {
        if (!node || !node.name) {
            continue;
        }

        if (defaultShippingOptionId === node.id) {
            defaultShippingOption = node.name;
        }

        shippingOptions.push({ value: node.name, label: node.name });
    }

    return { shippingOptions, defaultShippingOption };
}

const Wrapper = styled.div`
    margin-top: 1.6rem;
`;
